import { chainName, chainOf, pairOf } from "../components/ui/ChainAvatar";

/**
 * Bot names, one convention app-wide:
 * - inside a family (the pair is already on screen): "Bot 2"
 * - across families (overview lists): "USDC / WETH · Bot 2", next to a ChainAvatar
 * - when a list mixes chains and a pair repeats (needsChain), or the name stands alone (alert titles):
 *   "USDC / WETH · Arbitrum · Bot 2".
 * The raw id (ETH_USDC_WETH_2) belongs in a title / tooltip.
 */

/** Instance number at the end of a bot id: ETH_USDC_WETH_2 → 2; null for ids without one (DIPSTER). */
export const botNumber = (botId: string): number | null => {
  const match = botId.match(/_(\d+)$/);
  return match ? Number(match[1]) : null;
};

/** Family id of a bot: ETH_USDC_WETH_2 → ETH_USDC_WETH. */
export const familyOf = (botId: string): string => botId.replace(/_\d+$/, "");

/** Name inside a family: ETH_USDC_WETH_2 → "Bot 2" (ids without a number stay as they are). */
export const botShortLabel = (botId: string): string => {
  const n = botNumber(botId);
  return n == null ? botId : `Bot ${n}`;
};

/**
 * Name across families: ARB_USDC_WETH_2 → "USDC / WETH · Bot 2"; with `chain`,
 * "USDC / WETH · Arbitrum · Bot 2" (ids without a pair stay as they are).
 */
export const botLongLabel = (botId: string, opts: { chain?: boolean } = {}): string => {
  const pair = pairOf(botId);
  if (!pair) return botId;
  const n = botNumber(botId);
  return [pair, opts.chain ? chainName(chainOf(botId)) : null, n == null ? null : `Bot ${n}`]
    .filter(Boolean)
    .join(" · ");
};

/**
 * A name that may only wrap after its pair: "USDC / WETH ·" / "Ethereum · Bot 1" (every other space is
 * non-breaking), so a narrow column never strands the bot number on its own line or cuts it off.
 */
export const breakAfterPair = (label: string): string => {
  const [pair, ...rest] = label.split(" · ");
  const glue = (s: string) => s.replace(/ /g, "\u00A0");
  return rest.length > 0 ? `${glue(pair)}\u00A0· ${glue(rest.join(" · "))}` : glue(pair);
};

/**
 * True when the same pair runs on more than one chain among `ids` (USDC / WETH on Arbitrum and on
 * Ethereum): that list names its bots with their chain, botLongLabel(id, { chain: true }).
 */
export const needsChain = (ids: string[]): boolean => {
  const chainsByPair = new Map<string, string>();
  return ids.some((id) => {
    const pair = pairOf(id);
    if (!pair) return false;
    const chain = chainOf(id);
    const seen = chainsByPair.get(pair);
    if (seen === undefined) chainsByPair.set(pair, chain);
    return seen !== undefined && seen !== chain;
  });
};
